/**
 * A comment field that can @mention (library Calendar / Mention picker).
 *
 * Typing @ and the start of a name opens the picker above the field: the
 * brand's people, the client's first, the highlighted row in the accent tint
 * (radius 10, inset 6), and "They get a notification" at its foot. Arrow keys
 * move, Enter or Tab picks, Esc closes. A pick writes "@Name " into the text
 * and is sent as a mention only while that text is still there.
 */
import { forwardRef, useImperativeHandle, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { cn } from "@/utils/helpers";
import { initials } from "./commentModel";
import type { MentionPerson } from "./useMentionPeople";

export type MentionPick = { kind: "user" | "client"; id: string; name: string };

/** The mentions whose "@Name" is still in the text. */
export function mentionsIn(body: string, picked: readonly MentionPick[]): MentionPick[] {
  const seen = new Set<string>();
  return picked.filter((m) => {
    if (seen.has(m.id) || !body.includes(`@${m.name}`)) return false;
    seen.add(m.id);
    return true;
  });
}

/** The "@que" just before the caret, if the caret is in one. */
function queryAt(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const match = before.match(/(^|\s)@([^\s@]{0,24})$/);
  if (!match) return null;
  return { start: caret - match[2].length - 1, query: match[2] };
}

const MAX_ROWS = 6;

export interface MentionFieldHandle {
  focus: () => void;
}

export interface MentionFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Enter without Shift (and with no picker open). */
  onSubmit: () => void;
  /** Esc with no picker open. */
  onEscape?: () => void;
  people: readonly MentionPerson[];
  onPick: (pick: MentionPick) => void;
  id?: string;
  className?: string;
  placeholder?: string;
  ariaLabel: string;
  disabled?: boolean;
  /** Grow line by line where field-sizing is missing (Safari). */
  grow?: boolean;
  autoFocus?: boolean;
}

const MentionField = forwardRef<MentionFieldHandle, MentionFieldProps>(function MentionField(
  { value, onChange, onSubmit, onEscape, people, onPick, id, className, placeholder, ariaLabel, disabled, grow, autoFocus },
  ref,
) {
  const field = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => ({ focus: () => field.current?.focus() }), []);
  const [at, setAt] = useState<{ start: number; query: string } | null>(null);
  const [active, setActive] = useState(0);

  const matches = useMemo(() => {
    if (!at) return [];
    const q = at.query.toLowerCase();
    return people
      .filter((person) => !q || person.name.toLowerCase().split(/\s+/).some((word) => word.startsWith(q)) || person.name.toLowerCase().startsWith(q))
      .slice(0, MAX_ROWS);
  }, [at, people]);
  const open = !!at && matches.length > 0;
  const listId = id ? `${id}-mentions` : undefined;

  const sync = (text: string, caret: number) => {
    const next = queryAt(text, caret);
    setAt(next);
    if (next?.query !== at?.query) setActive(0);
  };

  const pick = (person: MentionPerson) => {
    if (!at) return;
    const caret = field.current?.selectionStart ?? value.length;
    const insert = `@${person.name} `;
    const next = value.slice(0, at.start) + insert + value.slice(caret);
    onChange(next);
    onPick({ kind: person.kind, id: person.id, name: person.name });
    setAt(null);
    const position = at.start + insert.length;
    requestAnimationFrame(() => {
      field.current?.focus();
      field.current?.setSelectionRange(position, position);
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (open) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const step = event.key === "ArrowDown" ? 1 : matches.length - 1;
        setActive((index) => (index + step) % matches.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        const person = matches[active];
        if (person) pick(person);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setAt(null);
        return;
      }
    }
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      onSubmit();
      return;
    }
    if (event.key === "Escape" && onEscape) {
      event.preventDefault();
      event.stopPropagation();
      onEscape();
    }
  };

  return (
    <span className="co-cmt-mention-wrap">
      <textarea
        ref={field}
        id={id}
        className={className}
        rows={1}
        dir="auto"
        maxLength={2000}
        value={value}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        placeholder={placeholder}
        role={people.length ? "combobox" : undefined}
        aria-expanded={people.length ? open : undefined}
        aria-controls={open ? listId : undefined}
        aria-autocomplete={people.length ? "list" : undefined}
        aria-activedescendant={open && listId ? `${listId}-${active}` : undefined}
        onChange={(event) => {
          onChange(event.target.value);
          sync(event.target.value, event.target.selectionStart ?? event.target.value.length);
          if (grow && !(typeof CSS !== "undefined" && CSS.supports?.("field-sizing", "content"))) {
            event.target.style.height = "auto";
            event.target.style.height = `${event.target.scrollHeight}px`;
          }
        }}
        onSelect={(event) => {
          const target = event.currentTarget;
          if (target.selectionStart === target.selectionEnd) sync(target.value, target.selectionStart ?? 0);
        }}
        onBlur={() => setAt(null)}
        onKeyDown={onKeyDown}
      />
      {open ? (
        <div className="co-cmt-mentions co-glass-chrome" role="presentation">
          <ul id={listId} role="listbox" aria-label="People to mention">
            {matches.map((person, index) => (
              <li
                key={person.id}
                id={listId ? `${listId}-${index}` : undefined}
                role="option"
                aria-selected={index === active}
                className={cn("co-cmt-mention", index === active && "is-active")}
                // Keeps the field focused, so the pick lands at the caret.
                onMouseDown={(event) => {
                  event.preventDefault();
                  pick(person);
                }}
                onMouseEnter={() => setActive(index)}
              >
                <span className="co-cmt-avatar" aria-hidden="true">
                  {initials(person.name)}
                </span>
                <span className="co-cmt-mention-name">{person.name}</span>
                <span className="co-cmt-org">{person.org}</span>
              </li>
            ))}
          </ul>
          <p className="co-cmt-mentions-hint">They get a notification</p>
        </div>
      ) : null}
    </span>
  );
});

export default MentionField;
